import React from 'react';
import { AppIcon } from '../components/AppIcon';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Simple and Clean */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl mb-6 text-white">About RealEsy</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Connecting the people who make real estate happen — brokerages, agents, and vendors — in one powerful platform.
            </p>
          </div>
        </div>
      </section>

      {/* Our Vision Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                <AppIcon name="aboutOurVision" size={28} alt="Our Vision" />
              </div>
              <h2 className="font-medium text-2xl text-gray-900">Our Vision</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Building a real estate ecosystem where everyone wins — brokerages, agents, and vendors alike.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                We've seen it first-hand: real estate professionals spend endless hours on things that should be simple.
                Coordinating installations, chasing updates, emailing files, managing payments — it's work that takes away from what truly matters: your clients, your time, and your growth.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                We built RealEsy because we believe technology should simplify, not complicate.
                It should help you work smarter, stay organized, and grow your business without adding noise to your day.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed">
                RealEsy connects the people who make real estate happen — brokerages, agents, and vendors — in one connected platform.
                From ordering and tracking installations to managing inventory, vendors, and documents, everything just flows.
                No chaos. No chasing. Just clarity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-6">
        <div className="border-t border-gray-200"></div>
      </div>

      {/* Our Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                <AppIcon name="aboutOurMission" size={28} alt="Our Mission" />
              </div>
              <h2 className="font-medium text-2xl text-gray-900">Our Mission</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                To give real estate professionals and vendors the tools to thrive — together.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6 rounded-r-lg">
                <p className="text-gray-800 mb-4">
                  <strong className="text-blue-900">For brokerages and agents,</strong> RealEsy delivers complete control and transparency.
                  You can track every order, manage inventory, and monitor vendor performance — all while saving hours each week.
                </p>
                <p className="text-gray-800 mb-0">
                  AI tools like SmartRead AI and SmartShow AI simplify daily tasks, from reading complex contracts to analyzing showings and client feedback.
                </p>
              </div>

              <div className="bg-orange-50 border-l-4 border-[#f58b01] p-6 mb-6 rounded-r-lg">
                <p className="text-gray-800 mb-4">
                  <strong className="text-orange-900">For vendors,</strong> we've built something equally powerful — tools that operate like a full business system.
                  Manage inventory, billing, product listings, sales tax, and automation in one place.
                </p>
                <p className="text-gray-800 mb-0">
                  Connect directly with clients and grow your business with consistency and confidence.
                </p>
              </div>

              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                We're not just creating software.
                We're building a community of real estate professionals and vendors who can rely on one another — powered by technology that respects their time and ambition.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                When everyone saves time and reduces operational waste, the benefits flow through the entire ecosystem.
                Vendors become more efficient, brokerages save more, and agents gain more time to do what they do best — sell, connect, and grow.
              </p>

              <p className="text-gray-900 text-xl leading-relaxed">
                When everyone wins, real estate moves forward together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-6">
        <div className="border-t border-gray-200"></div>
      </div>

      {/* Why We Exist Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                <AppIcon name="aboutWhyWeExist" size={28} alt="Why We Exist" />
              </div>
              <h2 className="font-medium text-2xl text-gray-900">Why We Exist</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Because we've been there. We know what it's like to lose hours managing what should take minutes.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Every feature, every workflow, and every AI insight we build comes from one simple goal:
              </p>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl mb-8 text-center">
                <p className="text-2xl text-gray-900 leading-relaxed mb-0">
                  To give professionals their time back and help them focus on what matters most — their clients, their relationships, and their growth.
                </p>
              </div>

              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                RealEsy is built for those who believe in doing things better together.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-gray-700 text-lg">
                  <AppIcon name="aboutBrokerages" size={26} alt="Brokerages" className="flex-shrink-0 mt-1" />
                  <span>For brokerages ready to modernize.</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700 text-lg">
                  <AppIcon name="aboutAgents" size={26} alt="Agents" className="flex-shrink-0 mt-1" />
                  <span>For agents ready to grow faster.</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700 text-lg">
                  <AppIcon name="aboutVendors" size={26} alt="Vendors" className="flex-shrink-0 mt-1" />
                  <span>For vendors ready to scale smarter.</span>
                </li>
              </ul>

              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                We're here to make a real difference in how real estate operates starting today, and evolving with you tomorrow.
              </p>

              <p className="text-xl text-gray-900 leading-relaxed">
                RealEsy — connecting real estate's brightest minds with technology that truly works for them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-4 text-white">Ready to Transform Your Real Estate Operations?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Join the ecosystem where everyone wins — brokerages, agents, and vendors working better together.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl"
            >
              Get Started Today
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
